<template>

  <div class="service_form_details retail_order_details">
    <b-row class="content-header">
      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="5"
        lg="5"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              {{ order.stringID }} - {{ order.customerName }}
            </h1>
            <p class="log_info">
              Order created by {{ order.customerName }} on {{ dateFormatWithTime(order.createdAt) }}<br>Last updated {{ dateFormatWithTime(order.updatedAt) }}
            </p>
          </b-col>
        </b-row>
      </b-col>

      <!-- Content Right -->
      <b-col
        class="content-header-right text-md-right mb-1 d-flex flex-wrap align-items-center justify-content-md-end"
        md="7"
        lg="7"
        cols="12"
      >
        <div class="user_block user__block-margin-adjust">
          <div class="user_avatar">
            <img :src="defaultAvatar">
          </div>
          <div class="user_info">
            <h6>Service Agent</h6>
            <h5>{{ order.serviceAgent || '–' }}</h5>
          </div>
        </div>
        <div class="user_block ac_type">
          <div class="user_avatar" />
          <div class="user_info">
            <h6>Order Status</h6>
            <h5
              class="text-capitalize"
              :class="`text-${resolveOrderStatusVariant(order.orderStatus)}`"
            >
              {{ order.orderStatus }}
            </h5>
          </div>
        </div>
        <div class="user_block file_payment_type user__block-margin-adjust">
          <div class="user_avatar" />
          <div class="user_info">
            <h6>Payment Status</h6>
            <h5
              class="text-capitalize"
              :class="`text-${resolvePaymentStatusVariant(order.paymentStatus)}`"
            >
              {{ order.paymentStatus }}
            </h5>
          </div>
        </div>
        <b-dropdown
          variant="flat-primary"
          no-caret
          class="px-1 right_align_dropdown"
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreHorizontalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>
          <b-dropdown-item @click="copyPaymentLink">
            <feather-icon icon="CopyIcon" size="20" />
            <span class="align-middle ml-50">Copy Payment Link</span>
          </b-dropdown-item>
          <b-dropdown-item @click="downloadTaxInvoice">
            <feather-icon icon="DownloadIcon" size="20" />
            <span class="align-middle ml-50">Download Tax Invoice</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canViewThisAction('update', 'RetailOrder')"
            :to="{ name: 'sales-retail-orders-register-payment', params: { id: order._id } }"
          >
            <feather-icon icon="DollarSignIcon" size="20" />
            <span class="align-middle ml-50">Register Payment/Refund</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canViewThisAction('update-status', 'RetailOrder')"
            v-b-modal.modal-update-order-status
          >
            <feather-icon icon="RotateCcwIcon" size="20" />
            <span class="align-middle ml-50">Change Order Status</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canViewThisAction('update-status', 'RetailOrder')"
            @click="resendTaxInvoice"
          >
            <feather-icon icon="SendIcon" size="20" />
            <span class="align-middle ml-50">Resend Tax Invoice</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canViewThisAction('update-status', 'RetailOrder') && order.orderStatus !== 'Cancelled'"
            @click="confirmCancelOrder"
          >
            <feather-icon icon="XSquareIcon" size="20" />
            <span class="align-middle ml-50">Cancel Order</span>
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
    </b-row>

    <!-- Customer Details / Related Service Request / Order Information -->
    <div class="order__details_top">
      <b-row class="match-height">
        <b-col md="4">
          <b-card>
            <b-card-title class="d-flex">
              <span>Customer Details</span>
              <b-button
                v-if="order.relatedServiceForm"
                variant="link"
                class="ml-auto p-0"
                :to="{ name: 'operation-service-forms-edit', params: { id: order.relatedServiceForm }, query: { section: 'applicant_detail' } }"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                  class="mr-25"
                />Edit
              </b-button>
            </b-card-title>
            <b-table-simple
              borderless
              responsive
              class="particulars_of_deceased d-none d-md-block"
            >
              <b-tbody>
                <b-tr>
                  <b-th width="140px">
                    Name
                  </b-th>
                  <b-td>
                    <b-link class="weight-600 align-middle detail-view-id">
                      {{ order.customerName }}
                    </b-link>
                    <feather-icon
                      v-b-tooltip.hover
                      icon="InfoIcon"
                      size="14"
                      class="ml-25 text-muted cursor-pointer"
                      title="View Customer Profile"
                    />
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th>Mobile Number</b-th>
                  <b-td>{{ order.customerMobile }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Email</b-th>
                  <b-td>{{ order.customerEmail }}</b-td>
                </b-tr>
                <b-tr>
                  <b-td colspan="2"><hr /></b-td>
                </b-tr>
                <b-tr>
                  <b-th>Niche No.</b-th>
                  <b-td>{{ order.nicheNo || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Pedestal No.</b-th>
                  <b-td>{{ order.pedestalNo || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Anling Lot No.</b-th>
                  <b-td>{{ order.anlingLotNo || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Others</b-th>
                  <b-td>{{ order.others || '–' }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card v-if="order.relatedBooking">
            <b-card-title>Related Appointment</b-card-title>
            <b-table-simple
              borderless
              responsive
              class="particulars_of_deceased d-none d-md-block"
            >
              <b-tbody>
                <b-tr>
                  <b-th width="140px">
                    Activity Type
                  </b-th>
                  <b-td>{{ order.relatedBooking.activityType ? order.relatedBooking.activityType.typeName : '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Start Date</b-th>
                  <b-td>{{ order.relatedBooking.start ? dateFormat(order.relatedBooking.start) : '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Start Time</b-th>
                  <b-td>
                    <span v-if="order.relatedBooking.start">
                      {{ timeFormatFromDateString(order.relatedBooking.start) }}
                      <span v-if="order.relatedBooking.start !== order.relatedBooking.end"> - {{ timeFormatFromDateString(order.relatedBooking.end) }}</span>
                    </span>
                    <span v-else>–</span>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th>Service Location</b-th>
                  <b-td>{{ order.serviceLocation || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Related Booking</b-th>
                  <b-td>
                    <b-link
                      class="weight-600 align-middle detail-view-id"
                      :to="{ name: 'appointment-bookings-show', params: { id: order.relatedBooking._id } }"
                    >
                      {{ order.relatedBookingStringID || '–' }}
                    </b-link>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th>Booking Status</b-th>
                  <b-td>
                    <span
                      class="text-capitalize"
                      :class="`text-${resolveBookingStatusVariant(order.relatedBooking.status)}`"
                    >
                      {{ order.relatedBooking.status ? order.relatedBooking.status.replace('-', ' ') : '–' }}
                    </span>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <div class="">
              <hr />
              <p class="mb-25 weight-600 mt-2">
                Appointment Remarks
              </p>
              <p class="text-muted mb-0">
                {{ order.relatedBooking.remarks || '–' }}
              </p>
            </div>
          </b-card>
          <b-card v-else>
            <b-card-title>Related Service Request</b-card-title>
            <b-table-simple
              borderless
              responsive
              class="particulars_of_deceased d-none d-md-block"
            >
              <b-tbody>
                <b-tr>
                  <b-th width="140px">
                    Service Type
                  </b-th>
                  <b-td>{{ order.serviceType || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Service Location</b-th>
                  <b-td>{{ order.serviceLocation || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Service Date</b-th>
                  <b-td>{{ order.serviceDate ? dateFormat(order.serviceDate) : '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Service Time</b-th>
                  <b-td>{{ order.serviceTime || '–' }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Related SR</b-th>
                  <b-td>
                    <b-link
                      v-if="order.relatedServiceRequest"
                      class="weight-600 align-middle detail-view-id"
                      :to="{ name: 'operation-service-requests-show', params: { id: order.relatedServiceRequest._id } }"
                    >
                      {{ order.relatedServiceRequestStringID || '–' }}
                    </b-link>
                    <span v-else>{{ order.relatedServiceRequestStringID || '–' }}</span>
                  </b-td>
                </b-tr>
                <b-tr>
                  <b-th>SR Status</b-th>
                  <b-td>
                    <span
                      v-if="order.relatedServiceRequest"
                      :class="`text-${resolveOrderStatusVariant(order.relatedServiceRequest.status)} text-capitalize`"
                    >
                      {{ order.relatedServiceRequest.status }}
                    </span>
                    <span v-else>–</span>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <div class="">
              <hr />
              <p class="mb-25 weight-600 mt-2">
                SR Remarks
              </p>
              <p class="text-muted mb-0">
                {{ (order.relatedServiceRequest && order.relatedServiceRequest.remarks) || '–' }}
              </p>
            </div>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card>
            <b-card-title>Order Information</b-card-title>
            <b-table-simple
              borderless
              responsive
              class="particulars_of_deceased d-none d-md-block"
            >
              <b-tbody>
                <b-tr>
                  <b-th width="150px">
                    Order Date
                  </b-th>
                  <b-td>{{ dateFormatWithTime(order.createdAt) }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Order No.</b-th>
                  <b-td>{{ order.stringID }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <div class="">
              <hr />
              <p class="mb-25 weight-600 mt-2 d-flex justify-content-between align-items-center">
                Order Remarks
                <span class="edit_button_block"
                   v-if="canViewThisAction('update', 'RetailOrder')"
                   v-b-tooltip.hover.top="'Edit Order Remarks'"
                   v-b-modal.modal-edit-order-remarks
                   @click="openEditOrderRemarks('order')"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="18"
                    class="cursor-pointer text-primary"
                  />
                </span>
              </p>
              <p class="text-muted mb-0">
                {{ order.orderRemarks || '–' }}
              </p>
            </div>
            <hr class="">
            <b-table-simple
              borderless
              responsive
              class="particulars_of_deceased d-none d-md-block mt-2"
            >
              <b-tbody>
                <b-tr>
                  <b-th width="185px">
                    Total Payable
                  </b-th>
                  <b-td>S${{ numberFormat(order.totalPayable) }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Amount Paid</b-th>
                  <b-td>S${{ numberFormat(order.amountPaid) }}</b-td>
                </b-tr>
                <b-tr>
                  <b-th>Outstanding Amount</b-th>
                  <b-td :class="outstandingAmount < 0 ? 'text-danger' : ''">
                    {{ outstandingAmountLabel }}
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <div class="">
              <hr />
              <p class="mb-25 weight-600 mt-2 d-flex justify-content-between align-items-center">
                Internal Remarks
                <span class="edit_button_block"
                  v-if="canViewThisAction('update', 'RetailOrder')"
                  v-b-tooltip.hover.top="'Edit Internal Remarks'"
                  v-b-modal.modal-edit-order-remarks
                  @click="openEditOrderRemarks('internal')"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="18"
                    class="ml-50 cursor-pointer text-primary"
                  />
                </span>
              </p>
              <p class="text-muted mb-0">
                {{ order.internalRemarks || '–' }}
              </p>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Item Purchased -->
      <b-card class="mb-0 p-2">
        <b-card-title class="d-flex align-items-center flex-wrap">
          <span>Item Purchased</span>
          <b-button
            v-if="canViewThisAction('update', 'RetailOrder') && editServiceDescriptionsRoute"
            variant="link"
            class="p-0 ml-2"
            :to="editServiceDescriptionsRoute"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
              class="mr-25"
            />Edit Items
          </b-button>
          <b-button
            v-if="canViewThisAction('update', 'RetailOrder') && !(order.appliedCoupon && order.appliedCoupon.couponCode)"
            variant="link"
            class="p-0 ml-2"
            @click="openAddCouponModal"
          >
            <feather-icon
              icon="PlusCircleIcon"
              size="14"
              class="mr-25"
            />Add Coupon
          </b-button>
          <span class="ml-auto d-flex align-items-center">
            <b-button
              v-if="canViewThisAction('update', 'RetailOrder') && order.orderStatus === 'Confirmed'"
              v-b-modal.modal-change-order-status
              v-b-tooltip.hover="completeOrderStockOutTooltip"
              variant="link"
              class="p-0"
              :disabled="!paymentCompleted || stockOutBlocked"
            >
              <feather-icon
                icon="LogOutIcon"
                size="14"
                class="mr-25"
              />Complete Order &amp; Stock Out
            </b-button>
          </span>
        </b-card-title>
        <div class="item__description_table">
          <b-table-simple
            responsive
            class="position-relative has_padding"
          >
            <b-thead>
              <b-tr class="group__tag">
                <b-th>#</b-th>
                <b-th>Item</b-th>
                <b-th>Inventory Item</b-th>
                <b-th>Quantity</b-th>
                <b-th>Unit Price (S$)</b-th>
                <b-th>Amount (S$)</b-th>
              </b-tr>
            </b-thead>
            <b-tbody class="white-body-background">
              <b-tr
                v-for="(item, index) in order.items"
                :key="index"
              >
                <b-td>{{ index + 1 }}</b-td>
                <b-td>
                  <b-link class="weight-600 align-middle detail-view-id">
                    {{ item.name }}
                  </b-link>
                </b-td>
                <b-td>
                  <b-img
                    v-if="item.retailItem || item.inventoryItem"
                    v-b-tooltip.hover.top="(item.retailItem || item.inventoryItem).name"
                    class="purchased-item-thumb"
                    :src="(item.retailItem || item.inventoryItem).image ? (item.retailItem || item.inventoryItem).image : defaultInventoryImage"
                  />
                  <span v-else>–</span>
                </b-td>
                <b-td>{{ item.quantity }}</b-td>
                <b-td>{{ numberFormat(item.unitPrice) }}</b-td>
                <b-td>{{ numberFormat(item.amount) }}</b-td>
              </b-tr>
              <tr class="row__has_bg has_border">
                <td colspan="4" />
                <td style="font-weight: 600!important">
                  Subtotal
                </td>
                <td style="font-weight: 600!important">{{ numberFormat(order.subtotal) }}</td>
              </tr>
              <tr class="row__has_bg">
                <td colspan="4" />
                <td style="font-weight: 600!important">
                  Inc. GST (9%)
                </td>
                <td style="font-weight: 600!important">({{ numberFormat(order.gstAmount) }})</td>
              </tr>
              <tr
                v-if="order.appliedCoupon && order.appliedCoupon.couponCode"
                class="row__has_bg"
              >
                <td colspan="4" />
                <td style="font-weight: 600!important">
                  {{ couponSummaryLabel }}
                </td>
                <td style="font-weight: 600!important">
                  <span class="d-flex align-items-center justify-content-start">
                    <span>– {{ numberFormat(order.appliedCoupon.discountAmount) }}</span>
                    <feather-icon
                      icon="Trash2Icon"
                      size="16"
                      class="text-danger cursor-pointer ml-1"
                      @click="confirmRemoveCoupon"
                    />
                  </span>
                </td>
              </tr>
              <tr class="row__has_bg last_child">
                <td colspan="4" />
                <td style="font-weight: 500!important; font-size: 18px!important;">
                  Total
                </td>
                <td style="font-weight: 500!important; font-size: 18px!important;">
                  ${{ numberFormat(order.totalPayable) }}
                </td>
              </tr>
            </b-tbody>
          </b-table-simple>
        </div>
        <div
          v-if="order.relatedServiceRequest && order.relatedServiceRequest.showServiceDescription"
          class="mt-2 pt-2 border-top px-2 pb-2"
        >
          <p class="mb-1 weight-600">
            Service Description
          </p>
          <b-table-simple
            borderless
            responsive
            class="service__description"
          >
            <b-tbody>
              <b-tr
                v-for="(opt, key) in order.relatedServiceRequest.serviceDescriptionValues"
                :key="'sr' + key"
              >
                <b-th
                  v-if="(opt.children && opt.children.length) || opt.value"
                  width="35%"
                  style="vertical-align: top; padding: 5px 0px !important;"
                >
                  {{ opt.key }}
                  <div>
                    <span style="font-style: normal; font-weight: 400; font-size: 14px;">
                      {{ opt.text }}
                    </span>
                  </div>
                </b-th>
                <b-td v-if="(opt.children && opt.children.length) || opt.value">
                  <div v-if="opt.children">
                    <div
                      v-for="(child, indx) in opt.children"
                      :key="'child' + indx"
                    >
                      <span
                        v-if="Array.isArray(child.value)"
                        class="d-flex"
                      >
                        <span class="mr-50">{{ child.key }}</span>
                        <div v-if="child.value.length">
                          <span
                            v-for="(option, index) in child.value"
                            :key="'cv' + index"
                          >
                            {{ option }}<span v-if="index + 1 != child.value.length">, </span>
                          </span>
                        </div>
                        <div v-else>
                          <span>-</span>
                        </div>
                      </span>
                      <span
                        v-else
                        class="d-flex"
                      >
                        <span class="mr-50">{{ child.key }}</span>
                        <div v-if="child.value">
                          <span
                            v-if="(child.type && ['increment', 'short-answer', 'long-answer'].includes(child.type))"
                            class="green-text"
                          >
                            {{ child.value }}
                          </span>
                          <span v-else>{{ child.value }}</span>
                        </div>
                        <div v-else>
                          <span>{{ child.type == 'title-and-text' ? '' : '-' }}</span>
                        </div>
                      </span>
                    </div>
                  </div>
                  <div v-else>
                    <span
                      v-if="Array.isArray(opt.value)"
                      class="d-flex"
                    >
                      <div v-if="opt.value.length">
                        <span
                          v-for="(option, index) in opt.value"
                          :key="'ov' + index"
                        >
                          {{ option }}<span v-if="index + 1 != opt.value.length">, </span>
                        </span>
                      </div>
                      <div v-else>
                        <span>-</span>
                      </div>
                    </span>
                    <span
                      v-else
                      class="d-flex"
                    >
                      <div v-if="opt.value">
                        <span
                          v-if="(opt.type && ['increment', 'short-answer', 'long-answer'].includes(opt.type))"
                          class="green-text"
                        >
                          {{ opt.value }}
                        </span>
                        <span v-else>{{ opt.value }}</span>
                      </div>
                      <div v-else>
                        <span>{{ opt.type == 'title-and-text' ? '' : '-' }}</span>
                      </div>
                    </span>
                  </div>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>
      </b-card>
    </div>

    <!-- Payment Summary -->
    <b-card
      id="payment-summary"
      class="mb-3 mt-1 p-2"
    >
      <b-card-title class="d-flex flex-wrap">
        <span>Payment Summary</span>
        <span class="ml-auto d-flex align-items-center">
          <b-button
            v-if="canViewThisAction('update', 'RetailOrder')"
            :to="{ name: 'sales-retail-orders-register-payment', params: { id: order._id } }"
            variant="link"
            class="p-0 mr-2"
          >
            Register Payment/Refund
          </b-button>
          <b-button
            variant="link"
            class="p-0"
            @click="copyPaymentLink"
          >
            Copy Payment Link
          </b-button>
        </span>
      </b-card-title>

      <div class="history_wrapper">
        <h6 class="mt-1">
          Payment History
        </h6>
        <b-table-simple
          responsive
          class="position-relative"
        >
          <b-thead>
            <b-tr>
              <b-th>Trx No.</b-th>
              <b-th>Payment Mode</b-th>
              <b-th>Amount (S$)</b-th>
              <b-th>Payment Reference</b-th>
              <b-th>Date/Time</b-th>
              <b-th>Processed By</b-th>
              <b-th>Remarks</b-th>
              <b-th>Action</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="(payment, index) in paymentHistory"
              :key="index"
            >
              <b-td>
                <b-link
                  class="weight-600 align-middle detail-view-id"
                  :to="{ name: 'sales-transactions-index', query: { trxNo: payment.stringID } }"
                >
                  {{ payment.stringID.replace(/^TX/i, '') }}
                </b-link>
              </b-td>
              <b-td>{{ payment.paymentMode }}</b-td>
              <b-td>{{ numberFormat(payment.amount) }}</b-td>
              <b-td>
                {{ payment.paymentReferenceLabel }}<br>
                <small class="text-muted">{{ payment.paymentReferenceNo }}</small>
              </b-td>
              <b-td>{{ dateFormatWithTime(payment.createdAt) }}</b-td>
              <b-td>
                <b-link class="weight-600 align-middle">
                  {{ payment.processedBy ? payment.processedBy.name : '–' }}
                </b-link>
              </b-td>
              <b-td>{{ payment.remarks || '–' }}</b-td>
              <b-td>
                <b-dropdown
                  variant="link"
                  no-caret
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item
                    v-if="canViewThisAction('update', 'RetailOrder')"
                    v-b-modal.modal-edit-payment-remarks
                    @click="openEditPaymentRemarks(payment)"
                  >
                    <span class="align-middle ml-50">Edit Remarks</span>
                  </b-dropdown-item>
                </b-dropdown>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>

      <div class="history_wrapper mt-2">
        <h6 class="mt-1">
          Non-Trade Receipts (SCLC)
        </h6>
        <b-table-simple
          responsive
          class="position-relative"
        >
          <b-thead>
            <b-tr>
              <b-th>Trx No.</b-th>
              <b-th width="30%">Item</b-th>
              <b-th>Amount (S$)</b-th>
              <b-th>Payment Type</b-th>
              <b-th>Remarks</b-th>
              <b-th>Created By</b-th>
              <b-th>Last Updated By</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="(receipt, index) in nonTradeReceipts"
              :key="index"
            >
              <b-td>
                <b-link class="weight-600 align-middle detail-view-id">
                  {{ receipt.trxNo }}
                </b-link><br>
                <small class="text-muted">{{ receipt.date }}</small>
              </b-td>
              <b-td>
                {{ receipt.item }}<br>
                <small class="text-muted">{{ receipt.itemNote }}</small>
              </b-td>
              <b-td>{{ numberFormat(receipt.amount) }}</b-td>
              <b-td>{{ receipt.paymentType }}</b-td>
              <b-td>{{ receipt.remarks }}</b-td>
              <b-td>
                {{ receipt.createdBy }}<br>
                <small class="text-muted">{{ receipt.createdAt }}</small>
              </b-td>
              <b-td>
                {{ receipt.lastUpdatedBy }}<br>
                <small class="text-muted">{{ receipt.lastUpdatedAt }}</small>
              </b-td>
            </b-tr>
            <tr class="row__has_bg last_child">
              <td colspan="5" />
              <td style="font-weight: 500!important; font-size: 18px!important;">
                Total Receipt
              </td>
              <td style="font-weight: 500!important; font-size: 18px!important;">
                ${{ numberFormat(totalReceiptAmount) }}
              </td>
            </tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </b-card>

    <!-- Attachments -->
    <b-card
      title="Attachments"
      class="mb-3 mt-3 p-2"
    >
      <input
        ref="hiddenOrderAttachmentInput"
        type="file"
        multiple
        capture
        accept="image/jpeg, image/png, application/pdf"
        class="d-none"
        @change="uploadOrderAttachments"
      >
      <template v-if="order.attachments.length">
        <div
          v-for="(file, key) in order.attachments"
          :key="key"
          class="order-attachment-card"
        >
          <div class="order-attachment-card__file-box">
            <b-img
              v-if="file.type && file.type.includes('image')"
              :src="require('@/assets/images/admin/doc_jpg.png')"
              class="order-attachment-card__icon"
              alt="File icon"
            />
            <b-img
              v-else
              :src="require('@/assets/images/admin/doc_file.png')"
              class="order-attachment-card__icon"
              alt="File icon"
            />
            <strong class="order-attachment-card__name">
              <b-link
                v-if="file.fileStorage"
                :to="{ name: 'file-view', params: { id: file.fileStorage } }"
                target="_blank"
              >
                {{ file.name }}
              </b-link>
              <template v-else>{{ file.name }}</template>
            </strong>
            <span class="order-attachment-card__size">{{ file.size }}mb</span>
            <feather-icon
              v-if="canViewThisAction('update', 'RetailOrder')"
              icon="XIcon"
              size="16"
              class="order-attachment-card__remove cursor-pointer"
              @click="removeOrderAttachment(file)"
            />
          </div>
          <div class="order-attachment-card__desc-row">
            <span
              v-if="file.description"
              class="order-attachment-card__description"
            >{{ file.description }}</span>
            <button
              v-else-if="canViewThisAction('update', 'RetailOrder')"
              v-b-modal.modal-order-attachment-description
              type="button"
              class="order-attachment-card__add-description"
              @click="openOrderAttachmentDescription(key)"
            >
              Add a description
            </button>
            <feather-icon
              v-if="canViewThisAction('update', 'RetailOrder')"
              v-b-modal.modal-order-attachment-description
              icon="EditIcon"
              size="14"
              class="text-primary cursor-pointer"
              @click="openOrderAttachmentDescription(key)"
            />
          </div>
        </div>
      </template>
      <b-row v-else>
        <b-col>
          <div class="empty_block">
            <h4>Nothing in here yet</h4>
            <b-button
              v-if="canViewThisAction('update', 'RetailOrder')"
              variant="link"
              @click="$refs.hiddenOrderAttachmentInput.click()"
            >
              <feather-icon
                icon="UploadIcon"
                size="20"
                class="mr-50"
              />
              <span
                class="align-middle"
                style="font-weight: 500; font-size: 14px;"
              >
                Upload
              </span>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Activity Logs -->
    <b-card class="mb-3 mt-3 p-2 order_details_activity__logs">
      <b-row
        class="content-header"
      >
        <!-- Content Left -->
        <b-col
          class="content-header-left mb-2 p-2"
          cols="12"
          md="12"
        >
          <h4>
            <feather-icon
              icon="ListIcon"
              class="mr-50"
              size="16"
            />
            <span class="align-middle">Activity Logs</span>
          </h4>
        </b-col>
      </b-row>
      <app-timeline>
        <app-timeline-item
          v-for="(log, key) in order.activityLog"
          :key="key"
          :variant="log.variant"
        >
          <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
            <h6>
              {{ log.title }}
              <span
                v-for="(mention, mentionKey) in log.mentions"
                :key="mentionKey"
                class="ml-25"
              >
                <b-avatar
                  size="20"
                  variant="light-primary"
                  class="align-middle"
                >
                  <span style="font-size: 10px;">{{ mention }}</span>
                </b-avatar>
              </span>
            </h6>
            <small class="text-muted">{{ dateFormatWithTime(log.time) }}</small>
          </div>
          <p
            v-if="log.description"
            class="text-muted mb-0"
          >
            {{ log.description }}
          </p>
        </app-timeline-item>
      </app-timeline>
    </b-card>

    <b-modal
      id="modal-payment-link-copied"
      ref="payment-link-copied-modal"
      centered
      hide-header
      hide-footer
    >
      <div class="text-center">
        <img src="@/assets/images/icons/checked_icon.svg" />
        <h4 class="mb-1">
          Payment Link Copied!
        </h4>
        <b-form-input
          :value="paymentLinkUrl"
          readonly
        />
        <p class="text-muted mb-2">
          Your customer is able to make payment for their items through this payment link.
        </p>
        <b-button
          variant="primary"
          @click="recopyPaymentLink"
        >
          Copy link again
        </b-button>
      </div>
    </b-modal>

    <b-modal
      id="modal-add-coupon"
      ref="add-coupon-modal"
      modal-class="status-update-modal"
      centered
      no-close-on-backdrop
      title="Add Coupon"
    >
      <b-form-group label="Enter Coupon Code">
        <b-input-group>
          <b-form-input
            v-model="couponCode"
            :state="couponState === 'invalid' ? false : null"
            :disabled="couponState === 'applying'"
            @input="onCouponInputChange"
          />
          <b-input-group-append v-if="couponState === 'invalid'">
            <b-input-group-text class="bg-transparent">
              <feather-icon
                icon="XIcon"
                size="14"
                class="text-danger cursor-pointer"
                @click="clearCouponInput"
              />
            </b-input-group-text>
          </b-input-group-append>
        </b-input-group>
        <small
          v-if="couponState === 'invalid'"
          class="text-danger d-block mt-50"
        >
          {{ couponError }}
        </small>
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          :disabled="!couponCode || couponState === 'applying'"
          @click="applyCoupon"
        >
          <b-spinner
            v-if="couponState === 'applying'"
            small
            class="mr-50"
          />Apply
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-edit-payment-remarks"
      modal-class="status-update-modal"
      centered
      no-close-on-backdrop
      title="Edit Remarks"
      @ok="savePaymentRemarks"
    >
      <b-form-textarea
        v-model="editingPaymentRemarksValue"
        placeholder="Optional"
        rows="6"
        no-resize
      />

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          @click="ok()"
        >
          Save Changes
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-edit-order-remarks"
      modal-class="status-update-modal"
      centered
      no-close-on-backdrop
      :title="editingRemarksField === 'internal' ? 'Edit Internal Remarks' : 'Edit Order Remarks'"
      @ok="saveOrderRemarks"
    >
      <b-form-textarea
        v-model="editingRemarksValue"
        placeholder="Optional"
        rows="6"
        no-resize
      />

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          @click="ok()"
        >
          Save Changes
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-order-attachment-description"
      size="lg"
      title="Attachment Description"
      modal-class="status-update-modal"
      centered
      no-stacking
      header-class="front-modal"
      @ok="saveOrderAttachmentDescription"
    >
      <b-row>
        <b-col md="12">
          <b-form-textarea
            v-model="orderAttachmentDescription"
            placeholder="E.g. Payment Schedule"
          />
        </b-col>
      </b-row>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!orderAttachmentDescription || orderAttachmentKey === ''"
          @click="ok()"
        >
          <span class="align-middle">Save</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-change-order-status"
      modal-class="status-update-modal complete-order-status-modal custom_design"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
    >
      <template #default="{ cancel, ok }">
        <div class="text-center complete-order-status-modal-content">
          <feather-icon
            icon="RotateCcwIcon"
            size="60"
            class="text-success mb-1"
          />
          <h3>Change Order Status to Completed?</h3>
          <p class="text-muted">
            Are you sure you want to change the order status to Completed? This indicates that the order has been verified and approved to proceed. Please ensure all order details are accurate before confirming.
          </p>
          <div class="d-flex justify-content-center mt-2">
            <b-button
              variant="outline-primary"
              class="mr-1"
              @click="cancel()"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              @click="confirmCompleteOrderStockOut(); ok()"
            >
              Yes, proceed
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-update-order-status"
      modal-class="status-update-modal"
      centered
      no-close-on-backdrop
      title="Update Order Status"
      @show="resetOrderStatusForm"
      @ok="submitUpdateOrderStatus"
    >
      <b-form-group
        v-if="!cancelledReactivationBlocked"
        label="Order Status"
      >
        <v-select
          v-model="orderStatusForm.orderStatus"
          :options="allOrderStatusOptions"
          :selectable="status => orderStatusOptions.includes(status)"
          :clearable="false"
        />
      </b-form-group>
      <p
        v-else
        class="text-muted mb-0"
      >
        No valid status change is available until the related SR/Appointment status changes.
      </p>

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="flat-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="success"
          :disabled="cancelledReactivationBlocked || !orderStatusOptions.includes(orderStatusForm.orderStatus)"
          @click="ok()"
        >
          Save Changes
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardTitle, BButton, BDropdown, BDropdownItem, BTableSimple, BThead, BTbody, BTr, BTh, BTd,
  BLink, BImg, BAvatar, BModal, BFormTextarea, BFormGroup, BFormInput,
  BInputGroup, BInputGroupAppend, BInputGroupText, BSpinner,
  VBModal, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ToastificationContentCustom from '../ToastificationContentCustom.vue'

const { canViewThisAction } = useAclUtils()

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BButton,
    BDropdown,
    BDropdownItem,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BLink,
    BImg,
    BAvatar,
    BModal,
    BFormTextarea,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupText,
    BSpinner,
    AppTimeline,
    AppTimelineItem,
    vSelect,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      canViewThisAction,
      // eslint-disable-next-line global-require
      defaultAvatar: require('@/assets/images/placeholder/placeholder_image.png'),
      // eslint-disable-next-line global-require
      defaultInventoryImage: require('@/assets/images/placeholder/placeholder_image.png'),
      editingPayment: null,
      editingPaymentRemarksValue: '',
      editingRemarksField: null,
      editingRemarksValue: '',
      orderAttachmentDescription: '',
      orderAttachmentKey: '',
      paymentLinkUrl: '',
      couponCode: '',
      couponState: 'idle',
      couponError: '',
      order: {
        items: [], attachments: [], activityLog: [], relatedServiceRequest: null, relatedBooking: null,
      },
      paymentHistory: [],
      // Static placeholder data — will be wired to the API later
      nonTradeReceipts: [
        {
          trxNo: 'B2026/11/000265',
          date: '01/06/2026',
          item: 'Ksitigarbha Sutra 地藏经 (S$568)',
          itemNote: 'MPL-MASTER CHANTING FEE',
          amount: 568.00,
          paymentType: 'Visa Card',
          remarks: 'Full Payment',
          createdBy: 'KW Pang',
          createdAt: '28/11/2016, 4.44pm',
          lastUpdatedBy: 'YB Tan',
          lastUpdatedAt: '28/11/2016, 7.10pm',
        },
        {
          trxNo: 'B2026/11/000266',
          date: '01/06/2026',
          item: 'Nirvana Offering: 6 vegetarians, Fruits & Cake 富贵供品: 六菜与供果发糕 S$68',
          itemNote: 'MPL-PRAYING ITEM FEE',
          amount: 68.00,
          paymentType: 'Visa Card',
          remarks: 'Full Payment',
          createdBy: 'KW Pang',
          createdAt: '28/11/2016, 4.44pm',
          lastUpdatedBy: 'YB Tan',
          lastUpdatedAt: '28/11/2016, 7.10pm',
        },
        {
          trxNo: 'B2026/11/000267',
          date: '01/06/2026',
          item: 'Premium Offering Set: Vegetarian Meat, Fish & Chicken & 3 vegetarians, Fruits & Cake 吉祥供品: 三牲三菜与供果发糕 S$88',
          itemNote: 'MPL-PRAYING ITEM FEE',
          amount: 88.00,
          paymentType: 'Visa Card',
          remarks: 'Full Payment',
          createdBy: 'KW Pang',
          createdAt: '28/11/2016, 4.44pm',
          lastUpdatedBy: 'YB Tan',
          lastUpdatedAt: '28/11/2016, 7.10pm',
        },
        {
          trxNo: 'B2026/11/000268',
          date: '01/06/2026',
          item: 'Heavenly Bag 天堂礼包 S$48',
          itemNote: 'MPL-PRAYING ITEM FEE',
          amount: 96.00,
          paymentType: 'Visa Card',
          remarks: 'Full Payment',
          createdBy: 'KW Pang',
          createdAt: '28/11/2016, 4.44pm',
          lastUpdatedBy: 'YB Tan',
          lastUpdatedAt: '28/11/2016, 7.10pm',
        },
        {
          trxNo: 'B2026/11/000269',
          date: '01/06/2026',
          item: '4-7/Hours 小时 (S$388)',
          itemNote: 'MPL-PRAYING ITEM FEE',
          amount: 388.00,
          paymentType: 'Visa Card',
          remarks: 'Full Payment',
          createdBy: 'KW Pang',
          createdAt: '28/11/2016, 4.44pm',
          lastUpdatedBy: 'YB Tan',
          lastUpdatedAt: '28/11/2016, 7.10pm',
        },
      ],
      orderStatusForm: {
        orderStatus: 'Pending',
      },
    }
  },

  computed: {
    editServiceDescriptionsRoute() {
      if (this.order.relatedServiceRequest) {
        return { name: 'operation-service-requests-edit-service-descriptions', params: { id: this.order.relatedServiceRequest._id } }
      }
      if (this.order.relatedBooking) {
        return { name: 'appointment-bookings-edit-service-descriptions', params: { id: this.order.relatedBooking._id } }
      }
      return null
    },
    outstandingAmount() {
      return (this.order.totalPayable || 0) - (this.order.amountPaid || 0)
    },
    outstandingAmountLabel() {
      if (this.outstandingAmount === 0) return '–'
      const sign = this.outstandingAmount < 0 ? '-' : ''
      return `${sign}S$${this.numberFormat(Math.abs(this.outstandingAmount))}`
    },
    // A Booking counts as "approved" once it's confirmed; a Service Request counts as
    // approved once it's moved past "awaiting approval", whether that's into "pending" (in
    // progress) or all the way to "completed"/"completed and paid". These mirror the backend's
    // canonical checks in helpers/retail-order-status-sync.js.
    relatedApproved() {
      if (this.order.relatedBooking) return this.order.relatedBooking.status === 'confirmed'
      if (this.order.relatedServiceRequest) return this.order.relatedServiceRequest.status !== 'awaiting approval'
      return false
    },
    relatedAwaitingApproval() {
      if (this.order.relatedBooking) return this.order.relatedBooking.status === 'pending-approval'
      if (this.order.relatedServiceRequest) return this.order.relatedServiceRequest.status === 'awaiting approval'
      return false
    },
    orderStatusOptions() {
      if (this.order.orderStatus === 'Pending') return ['Cancelled']
      if (this.order.orderStatus === 'Confirmed') return ['Completed', 'Cancelled']
      if (this.order.orderStatus === 'Completed') return ['Confirmed', 'Cancelled']

      if (this.order.orderStatus === 'Cancelled') {
        const options = []
        if (this.relatedApproved) options.push('Confirmed')
        if (this.relatedAwaitingApproval) options.push('Pending')
        return options
      }

      return []
    },
    // The dropdown itself shows every order status (so staff can see the full picture), with
    // the current status and any status not reachable from it (per orderStatusOptions above)
    // disabled rather than hidden.
    allOrderStatusOptions() {
      return ['Pending', 'Confirmed', 'Completed', 'Cancelled']
    },
    cancelledReactivationBlocked() {
      return this.order.orderStatus === 'Cancelled' && this.orderStatusOptions.length === 0
    },
    stockOutBlocked() {
      return !this.relatedApproved
    },
    paymentCompleted() {
      return this.order.paymentStatus === 'Fully Paid' || this.order.paymentStatus === 'Overpaid'
    },
    completeOrderStockOutTooltip() {
      if (!this.paymentCompleted) return 'Payment must be completed before this order can be marked as Completed and stocked out'
      if (this.stockOutBlocked) return 'Cannot stock out while the related service request/booking is awaiting approval'
      return ''
    },
    totalReceiptAmount() {
      return this.nonTradeReceipts.reduce((sum, receipt) => sum + receipt.amount, 0)
    },
    couponSummaryLabel() {
      const coupon = this.order.appliedCoupon
      if (!coupon) return ''

      return `Coupon: ${coupon.couponTitle}`
    },
  },

  mounted() {
    this.fetchOrderDetail()
  },

  methods: {
    resetOrderStatusForm() {
      // Show the order's real current status first, not the only alternative it could move
      // to - staff must actively pick one of the (enabled) valid targets to change anything.
      this.orderStatusForm.orderStatus = this.order.orderStatus
    },
    resolveOrderStatusVariant(status) {
      if (status === 'Confirmed') return 'info'
      if (status === 'Completed') return 'success'
      if (status === 'Cancelled') return 'danger'
      return 'warning'
    },
    resolvePaymentStatusVariant(status) {
      if (status === 'Fully Paid') return 'success'
      if (status === 'Partially Paid') return 'warning'
      if (status === 'Overpaid') return 'info'
      if (status === 'Refunded') return 'secondary'
      return 'secondary'
    },
    resolveBookingStatusVariant(status) {
      if (status === 'confirmed') return 'success'
      if (status === 'pending-payment') return 'warning'
      if (status === 'pending-approval') return 'warning'
      if (status === 'waiting-list') return 'secondary'
      if (status === 'draft') return 'secondary'
      if (status === 'cancelled') return 'danger'
      return 'primary'
    },
    fetchOrderDetail() {
      this.$http.get(`sales/retail-orders/${this.$route.params.id}/show`)
        .then(response => {
          this.order = response.data.data
          this.paymentHistory = response.data.paymentHistory

          if (this.$route.hash === '#payment-summary') {
            this.$nextTick(() => {
              const el = document.getElementById('payment-summary')
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
          }

          if (this.$route.query.updated) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Order updated successfully',
                text: `${this.order.stringID} - ${this.order.customerName} has been updated successfully.`,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
            this.$router.replace({ query: {} }).catch(() => {})
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    openEditPaymentRemarks(payment) {
      this.editingPayment = payment
      this.editingPaymentRemarksValue = payment.remarks
    },
    openEditOrderRemarks(field) {
      this.editingRemarksField = field
      this.editingRemarksValue = (field === 'internal' ? this.order.internalRemarks : this.order.orderRemarks) || ''
    },
    saveOrderRemarks() {
      const formData = new FormData()
      formData.append('orderRemarks', this.editingRemarksField === 'internal' ? (this.order.orderRemarks || '') : this.editingRemarksValue)
      formData.append('internalRemarks', this.editingRemarksField === 'internal' ? this.editingRemarksValue : (this.order.internalRemarks || ''))

      this.$http.patch(`sales/retail-orders/${this.order._id}/remarks/update`, formData)
        .then(response => {
          this.order.orderRemarks = response.data.data.orderRemarks
          this.order.internalRemarks = response.data.data.internalRemarks
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    uploadOrderAttachments(e) {
      if (!e.target.files.length) return

      const acceptedTypes = ['image/jpeg', 'image/png', 'application/pdf']
      const formData = new FormData()
      let hasValidFile = false

      Array.from(e.target.files).forEach(file => {
        if (!acceptedTypes.includes(file.type)) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Unsupported file type',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return
        }
        if (file.size > 20 * 1024 * 1024) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Can't add files more than 20mb",
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return
        }
        formData.append('attachments', file)
        hasValidFile = true
      })

      e.target.value = ''
      if (!hasValidFile) return

      this.$http.post(`sales/retail-orders/${this.order._id}/attachment/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(response => {
          this.order.attachments = response.data.data.attachments
          this.$toast({
            component: ToastificationContent,
            props: {
              title: response.data.message || '',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    removeOrderAttachment(file) {
      this.$http.post(`sales/retail-orders/${this.order._id}/attachment/remove`, { attachmentId: file._id })
        .then(response => {
          this.order.attachments = response.data.data.attachments
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    openOrderAttachmentDescription(key) {
      this.orderAttachmentKey = key
      this.orderAttachmentDescription = this.order.attachments[key].description || ''
    },
    saveOrderAttachmentDescription() {
      if (this.orderAttachmentKey === '') return

      const attachment = this.order.attachments[this.orderAttachmentKey]
      const formData = new FormData()
      formData.append('attachmentId', attachment._id)
      formData.append('description', this.orderAttachmentDescription)

      this.$http.post(`sales/retail-orders/${this.order._id}/attachment/save-description`, formData)
        .then(response => {
          this.order.attachments = response.data.data.attachments
          this.orderAttachmentKey = ''
          this.orderAttachmentDescription = ''
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    savePaymentRemarks() {
      if (!this.editingPayment) return

      const formData = new FormData()
      formData.append('remarks', this.editingPaymentRemarksValue || '')

      this.$http.patch(`sales/transactions/${this.editingPayment._id}/remarks/update`, formData)
        .then(response => {
          this.editingPayment.remarks = response.data.data.remarks
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    // Marks the order Completed, then hands off its tagged line items to the real
    // Inventory Stock Out flow (inventory-stock-out) so staff review/adjust quantities
    // and stores there and confirm through its own existing "Proceed to Stock-Out?"
    // dialog - this endpoint only changes the order status, it does not move any stock
    // itself despite the route name (see completeOrderAndStockOut in the backend).
    confirmCompleteOrderStockOut() {
      this.$http.patch(`sales/retail-orders/${this.order._id}/complete-and-stock-out`)
        .then(response => {
          this.order.orderStatus = response.data.data.orderStatus
          this.$toast({
            component: ToastificationContentCustom,
            props: {
              title: 'Order Marked as Completed',
              text: `Order <span class="text-bold-black">${this.order.stringID}</span> has been marked as <span class="text-bold-black">Completed</span>.`,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
          this.redirectToStockOut()
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    redirectToStockOut() {
      const stockOutItems = (this.order.items || [])
        .filter(item => item.inventoryItem)
        .map(item => ({
          item: item.inventoryItem._id,
          itemName: item.inventoryItem.name,
          sku: item.inventoryItem.sku,
          image: item.inventoryItem.image,
          quantity: item.quantity,
        }))

      if (!stockOutItems.length) return

      localStorage.removeItem('storeVal')
      localStorage.setItem('items', JSON.stringify(stockOutItems))
      localStorage.setItem('stockOutRemarks', `${this.order.stringID} - ${this.order.customerName}`)
      localStorage.setItem('stockOutFromOrder', JSON.stringify({ id: this.order._id, stringID: this.order.stringID }))
      this.$router.push({ name: 'inventory-stock-out' })
    },
    resendTaxInvoice() {
      this.$swal({
        title: 'Re-send Tax Invoice?',
        html: `An email containing the order details and tax invoice will be sent to ${this.order.customerEmail}.`,
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/mail.png'),
        imageWidth: 80,
        imageHeight: 80,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Go back',
        confirmButtonText: 'Send Email',
        customClass: {
          actions: 'retail_footer',
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.$http.post('sales/retail-orders/email-invoice', { orderIds: [this.order._id] })
              .then(() => {
                this.$swal({
                  title: 'The tax invoice has been sent',
                  // eslint-disable-next-line global-require
                  imageUrl: require('@/assets/images/icons/success.png'),
                  imageWidth: 80,
                  imageHeight: 80,
                  showCancelButton: false,
                  showConfirmButton: false,
                  buttonsStyling: false,
                })
              })
              .catch(error => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              })
          }
        })
    },
    confirmCancelOrder() {
      this.$swal({
        title: 'Cancel this order?',
        text: 'Are you sure you want to cancel this order? This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning-square.png'),
        imageWidth: 80,
        imageHeight: 80,
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel order',
        cancelButtonText: 'Go back',
        reverseButtons: true,
        customClass: {
          actions: 'retail_footer',
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-default',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.$http.patch(`sales/retail-orders/${this.order._id}/cancel`)
              .then(response => {
                this.order.orderStatus = response.data.data.orderStatus
                this.orderStatusForm.orderStatus = response.data.data.orderStatus
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: response.data.message,
                    icon: 'CheckIcon',
                    variant: 'success',
                  },
                })
              })
              .catch(error => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              })
          }
        })
    },
    submitUpdateOrderStatus() {
      const previousStatus = this.order.orderStatus
      const formData = new FormData()
      formData.append('orderStatus', this.orderStatusForm.orderStatus)

      this.$http.patch(`sales/retail-orders/${this.order._id}/status/update`, formData)
        .then(response => {
          this.order.orderStatus = response.data.data.orderStatus
          this.$toast({
            component: ToastificationContentCustom,
            props: {
              title: 'Status updated successfully',
              text: `<span style="font-size: 14px; color: #5E5873;"><span style="font-size: 14px; color: #000000; font-weight: 600;">${this.order.stringID}</span> status has been updated from <span style="font-size: 14px; color: #000000; font-weight: 600;">${previousStatus}</span> to <span style="font-size: 14px; color: #000000; font-weight: 600;">${this.order.orderStatus}</span></span>`,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    copyPaymentLink() {
      this.$http.post(`sales/retail-orders/${this.order._id}/payment-link`)
        .then(async response => {
          this.paymentLinkUrl = response.data.link
          try {
            await navigator.clipboard.writeText(this.paymentLinkUrl)
            this.$root.$emit('bv::show::modal', 'modal-payment-link-copied', '')
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Unable to copy payment link',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    downloadTaxInvoice() {
      this.$http.post(`sales/retail-orders/${this.order._id}/invoice/pdf/download`, null, {
        headers: { Accept: 'application/pdf' },
        responseType: 'arraybuffer',
      })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `Tax-Invoice-${this.order.stringID}.pdf`
          link.click()
          link.remove()
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Unable to download tax invoice',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    openAddCouponModal() {
      this.couponCode = ''
      this.couponState = 'idle'
      this.couponError = ''
      this.$root.$emit('bv::show::modal', 'modal-add-coupon', '')
    },
    onCouponInputChange() {
      if (this.couponState === 'invalid') {
        this.couponState = 'idle'
        this.couponError = ''
      }
    },
    clearCouponInput() {
      this.couponCode = ''
      this.couponState = 'idle'
      this.couponError = ''
    },
    applyCoupon() {
      if (!this.couponCode || this.couponState === 'applying') return

      this.couponState = 'applying'
      const appliedCode = this.couponCode
      const formData = new FormData()
      formData.append('couponCode', appliedCode)

      this.$http.post(`sales/retail-orders/${this.order._id}/coupon/apply`, formData)
        .then(response => {
          this.couponState = 'idle'
          this.couponCode = ''
          this.$root.$emit('bv::hide::modal', 'modal-add-coupon', '')
          this.fetchOrderDetail()

          const updatedOrder = response.data.data || {}
          const newOutstanding = (updatedOrder.totalPayable || 0) - (updatedOrder.amountPaid || 0)
          const overpaidHtml = newOutstanding < 0
            ? ` The order is now overpaid by <span class="text-success">S$${this.numberFormat(Math.abs(newOutstanding))}</span>. Please register a refund before proceeding.`
            : ''

          this.$swal({
            title: 'Coupon applied!',
            html: `Coupon <b>${appliedCode}</b> has been applied successfully.${overpaidHtml}`,
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Got it',
            customClass: {
              confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
          }).then(() => {
            this.$toast({
              component: ToastificationContentCustom,
              props: {
                title: 'Coupon added successfully',
                text: `<span style="font-size: 14px; color: #5E5873;">Coupon <span style="font-size: 14px; color: #000000; font-weight: 600;">${appliedCode}</span> has been added successfully.</span>`,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
          })
        })
        .catch(error => {
          this.couponState = 'invalid'
          this.couponError = error.data.message ?? 'This coupon code is invalid.'
        })
    },
    confirmRemoveCoupon() {
      this.$swal({
        title: 'Remove Applied Coupon?',
        html: 'Removing this coupon will update the order total and may affect the outstanding payment amount. <br><br>Are you sure you want to continue?',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/delete_red.png'),
        imageWidth: 80,
        imageHeight: 80,
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Yes, Remove Coupon',
        customClass: {
          actions: 'retail_footer',
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.removeCoupon()
          }
        })
    },
    removeCoupon() {
      const removedCode = this.order.appliedCoupon ? this.order.appliedCoupon.couponCode : ''

      this.$http.delete(`sales/retail-orders/${this.order._id}/coupon/remove`)
        .then(response => {
          this.fetchOrderDetail()

          const updatedOrder = response.data.data || {}
          const outstanding = (updatedOrder.totalPayable || 0) - (updatedOrder.amountPaid || 0)
          let outstandingHtml = ''
          if (outstanding > 0) {
            outstandingHtml = ` The order now has an outstanding balance of <span class="text-danger">S$${this.numberFormat(outstanding)}</span>. Please collect the remaining payment and register the payment before proceeding.`
          } else if (outstanding < 0) {
            outstandingHtml = ` The order is now overpaid by <span class="text-success">S$${this.numberFormat(Math.abs(outstanding))}</span>. Please register a refund before proceeding.`
          }

          this.$swal({
            title: 'Coupon removed!',
            html: `Coupon <b>${removedCode}</b> has been removed successfully.${outstandingHtml}`,
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Got it',
            customClass: {
              confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
          }).then(() => {
            this.$toast({
              component: ToastificationContentCustom,
              props: {
                title: 'Coupon removed successfully',
                text: `<span style="font-size: 14px; color: #5E5873;">Coupon <span style="font-size: 14px; color: #000000; font-weight: 600;">${removedCode}</span> has been removed successfully.</span>`,
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
          })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    async recopyPaymentLink() {
      try {
        await navigator.clipboard.writeText(this.paymentLinkUrl)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Unable to copy payment link',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.purchased-item-thumb {
  box-sizing: border-box;
  width: 38px;
  height: 38px;
  border: 1px solid #D2DCEA;
  border-radius: 5px;
  object-fit: cover;
}
.complete-order-status-modal-content {
  padding: 20px;
}
.order-attachment-card {
  width: 320px;
  max-width: 100%;
  margin-bottom: 20px;

  &__file-box {
    display: flex;
    align-items: center;
    background: #F4F5F7;
    border-radius: 8px;
    padding: 10px 12px;
  }

  &__icon {
    width: 26px;
    height: auto;
    margin-right: 10px;
    flex-shrink: 0;
  }

  &__name {
    font-weight: 500;
    font-size: 14px;
    color: #1B1B1B;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__size {
    color: #9CA3AF;
    font-size: 13px;
    margin-left: 10px;
    flex-shrink: 0;
  }

  &__remove {
    color: #104D9D;
    margin-left: 10px;
    flex-shrink: 0;
  }

  &__desc-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
  }

  &__description {
    font-size: 13px;
    color: #333;
  }

  &__add-description {
    padding: 0;
    border: none;
    background: none;
    color: #104D9D;
    font-size: 13px;
    line-height: 18px;
  }
}
</style>

<style lang="scss">
// modal-class renders on the .modal wrapper bootstrap-vue teleports outside this
// component's own template, so it falls outside scoped styles above - kept global here.
.complete-order-status-modal .modal-content {
  border-radius: 12px;
}
</style>
